import type { ScopeContext } from '@sentry/types';
import { parseError } from '@snapchat/core';

import type { BaseEventListenerProps } from './AbstractEventListener';
import { AbstractEventListener } from './AbstractEventListener';
import type { BasePermission, EmptyObject, FlatContext, LoggedEvent } from './loggingTypes';
import { LoggingEventType } from './loggingTypes';

/**
 * Context for sentry logs.
 *
 * Note that we omit 'level' because it's incompatible between server and client libs. TODO: Add
 * level back when we can rectify this situation.
 */

export type SentryContext = Omit<ScopeContext, 'level'>;

export type SentryCapturer = (error: Error, context: Partial<SentryContext>) => void;

export interface SentryEventListenerProps<
  LoggingContext extends EmptyObject,
  CustomEvents extends EmptyObject
> extends BaseEventListenerProps<LoggingContext, CustomEvents> {
  getFlatContext: (context: Partial<LoggingContext>) => FlatContext;
}

export abstract class AbstractSentryEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> extends AbstractEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  protected readonly props: SentryEventListenerProps<LoggingContext, CustomEvents>;
  private captureException: SentryCapturer | undefined;

  constructor(props: SentryEventListenerProps<LoggingContext, CustomEvents>) {
    super({
      ...props,
      requiredPermissions: ['logging'],
    });
    this.props = props;
  }

  /** @override */
  protected init = async (): Promise<void> => {
    this.captureException = await this.getExceptionCapturer();
  };

  /** @override */
  protected logEventInternal(
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ): void {
    switch (event.type) {
      case LoggingEventType.ERROR: {
        const error = event.message
          ? new Error(event.message)
          : parseError(event.error ?? 'UnknownError');

        const flatContext = this.props.getFlatContext(context) ?? {};
        const sentryContext = Object.fromEntries(
          Object.entries(flatContext).filter(([, value]) => !!value)
        );

        // Taken from: https://github.com/getsentry/sentry-javascript/blob/master/packages/react/src/errorboundary.tsx
        // Note that we use 'generic' because we don't have enough captured contexts to group them
        // by subcategories.
        this.captureException!(error, {
          contexts: { generic: sentryContext },
        });
        break;
      }
    }
  }

  /** Lazy loads the library for capturing into sentry. */
  protected abstract getExceptionCapturer: () => Promise<SentryCapturer>;

  /** @override */
  protected flushInternal: () => Promise<unknown> = () => Promise.resolve();
}
